import { useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { RegistrationForm } from "@/components/RegistrationForm";
import { content, galleryItems, socialLinks } from "@/siteContent";

const LOGO_URL = "/logo.png";

const SectionHeading = ({ title, description, eyebrow, align = "center", testId }) => (
  <div className={`space-y-4 ${align === "center" ? "mx-auto max-w-3xl text-center" : "max-w-3xl text-left"}`}>
    {eyebrow ? (
      <p className="section-eyebrow" data-testid={`${testId}-eyebrow`}>
        {eyebrow}
      </p>
    ) : null}
    <h2 className="text-4xl font-semibold uppercase tracking-[0.08em] text-white sm:text-5xl lg:text-6xl" data-testid={`${testId}-title`}>
      {title}
    </h2>
    {description ? (
      <p className="mx-auto text-sm leading-7 text-slate-300 sm:text-base" data-testid={`${testId}-description`}>
        {description}
      </p>
    ) : null}
  </div>
);

function App() {
  const [language, setLanguage] = useState("fr");
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeImage, setActiveImage] = useState(null);

  // Dynamic data from API
  const [apiGallery, setApiGallery] = useState(null);
  const [apiMatches, setApiMatches] = useState(null);
  const [apiTestimonials, setApiTestimonials] = useState(null);
  const [apiVideos, setApiVideos] = useState(null);

  const currentContent = content[language];
  const alternateLanguage = language === "fr" ? "ar" : "fr";
  const mapUrl = "https://www.google.com/maps?q=CLUB+AL+BURAQ+SPORTIF+AIN+ATIQ&output=embed";

  // Fetch dynamic data from PHP API
  useEffect(() => {
    const fetchData = async (endpoint, setter) => {
      try {
        const res = await fetch(`/api/${endpoint}.php`);
        if (res.ok) setter(await res.json());
      } catch (e) { /* fallback to static content */ }
    };
    fetchData("gallery", setApiGallery);
    fetchData("matches", setApiMatches);
    fetchData("testimonials", setApiTestimonials);
    fetchData("videos", setApiVideos);
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.body.classList.toggle("arabic-mode", language === "ar");
  }, [language]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
      setShowScrollTop(window.scrollY > 420);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [language]);

  useEffect(() => {
    document.body.style.overflow = menuOpen || activeImage ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, activeImage]);

  const toggleLanguage = () => setLanguage(alternateLanguage);
  const titleClassName = language === "ar" ? "font-arabic" : "font-display";
  const textClassName = language === "ar" ? "font-arabic" : "font-body";

  return (
    <div className={`App page-shell ${textClassName}`}>
      <Toaster richColors position="top-center" />

      <div className="page-glow page-glow-blue" aria-hidden="true" />
      <div className="page-glow page-glow-orange" aria-hidden="true" />

      <header className={`site-navbar ${isScrolled ? "site-navbar-scrolled" : ""}`} data-testid="site-navbar">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-5 py-4 sm:px-6 lg:px-8">
          <a href="#hero" className="flex items-center gap-3 no-underline" data-testid="navbar-logo-link">
            <img src={LOGO_URL} alt="Club Al Buraq Sportif logo" className="h-14 w-14 rounded-full object-contain ring-1 ring-white/10" loading="eager" data-testid="navbar-logo-image" />
            <div>
              <p className={`text-2xl uppercase tracking-[0.3em] text-white ${titleClassName}`} data-testid="navbar-brand-title">
                {currentContent.brand}
              </p>
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400" data-testid="navbar-brand-subtitle">
                {currentContent.academyBadge}
              </p>
            </div>
          </a>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-navigation">
            {currentContent.nav.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="text-sm font-medium uppercase tracking-[0.18em] text-slate-200 transition-colors duration-300 hover:text-[#5dade2]"
                data-testid={`navbar-link-${item.id}`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={toggleLanguage}
              className="h-11 rounded-full border-white/15 bg-white/5 px-5 text-sm font-semibold text-white backdrop-blur-sm transition hover:border-[#5dade2] hover:bg-white/10"
              data-testid="language-switch-button"
            >
              {currentContent.languageSwitch}
            </Button>

            <button
              type="button"
              onClick={() => setMenuOpen((previous) => !previous)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition hover:border-[#f39c12] hover:bg-white/10 lg:hidden"
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              <i className={`fa-solid ${menuOpen ? "fa-xmark" : "fa-bars"} text-lg`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {menuOpen ? (
        <div className="mobile-menu-overlay lg:hidden" data-testid="mobile-menu-overlay">
          <div className="mobile-menu-panel">
            {currentContent.nav.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={() => setMenuOpen(false)}
                className="mobile-menu-link"
                data-testid={`mobile-menu-link-${item.id}`}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      ) : null}

      <main className="relative z-10 overflow-hidden">
        <section id="hero" className="hero-section px-5 pb-20 pt-32 sm:px-6 sm:pb-24 lg:px-8 lg:pb-28 lg:pt-36">
          <div className="mx-auto grid min-h-[calc(100vh-7rem)] max-w-7xl items-center gap-14 lg:grid-cols-[1.15fr_0.85fr]">
            <Reveal className="space-y-7">
              <span className="hero-pill" data-testid="hero-location-badge">
                <i className="fa-solid fa-location-dot text-[#f39c12]" aria-hidden="true" />
                {currentContent.hero.location}
              </span>

              <div className="space-y-5">
                <p className="section-eyebrow" data-testid="hero-eyebrow">
                  {currentContent.academyBadge}
                </p>
                <h1 className={`max-w-4xl text-5xl uppercase leading-none text-white sm:text-6xl lg:text-7xl ${titleClassName}`} data-testid="hero-main-title">
                  {currentContent.hero.title}
                </h1>
                <p className="text-lg text-[#5dade2] sm:text-xl" data-testid="hero-alternate-title">
                  {currentContent.hero.alternateTitle}
                </p>
              </div>

              <div className="space-y-3">
                <p className="text-xl italic text-slate-100 sm:text-2xl" data-testid="hero-slogan-primary">
                  “{currentContent.hero.slogan}”
                </p>
                <p className="text-sm italic text-slate-400 sm:text-base" data-testid="hero-slogan-secondary">
                  {currentContent.hero.alternateSlogan}
                </p>
              </div>

              <p className="max-w-2xl text-base leading-8 text-slate-300 sm:text-lg" data-testid="hero-description">
                {currentContent.hero.description}
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="#registration" data-testid="hero-primary-cta-link">
                  <Button className="h-12 rounded-full bg-gradient-to-r from-[#2980b9] to-[#f39c12] px-7 text-base font-semibold text-slate-950 transition-transform duration-300 hover:-translate-y-0.5">
                    {currentContent.hero.primaryCta}
                  </Button>
                </a>
                <a href="#about" data-testid="hero-secondary-cta-link">
                  <Button variant="outline" className="h-12 rounded-full border-white/15 bg-white/5 px-7 text-base font-semibold text-white backdrop-blur-sm transition hover:border-[#5dade2] hover:bg-white/10">
                    {currentContent.hero.secondaryCta}
                  </Button>
                </a>
              </div>
            </Reveal>

            <Reveal className="flex justify-center lg:justify-end" delay={120}>
              <div className="hero-logo-frame floating-logo" data-testid="hero-logo-frame">
                <div className="hero-logo-glow" aria-hidden="true" />
                <img src={LOGO_URL} alt="Club Al Buraq Sportif emblem" className="hero-logo-image" loading="eager" data-testid="hero-logo-image" />
              </div>
            </Reveal>
          </div>
        </section>

        <section id="about" className="section-shell px-5 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-7xl items-center gap-10 rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-md sm:p-8 lg:grid-cols-[1.1fr_0.9fr] lg:p-10">
            <Reveal className="space-y-6">
              <SectionHeading
                title={currentContent.about.title}
                description={currentContent.about.text}
                eyebrow={language === "fr" ? "Notre Académie" : "أكاديميتنا"}
                align="left"
                testId="about-section"
              />

              <div className="flex flex-wrap gap-3">
                {currentContent.about.stats.map((stat, index) => (
                  <span
                    key={stat}
                    className="rounded-full border border-white/10 bg-[#0d1d31] px-4 py-2 text-sm font-medium text-slate-100"
                    data-testid={`about-stat-${index + 1}`}
                  >
                    {stat}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal className="flex items-center justify-center" delay={120}>
              <div className="about-logo-card" data-testid="about-logo-card">
                <img src={LOGO_URL} alt="Club Al Buraq logo" className="max-h-[26rem] w-full object-contain" loading="lazy" data-testid="about-logo-image" />
              </div>
            </Reveal>
          </div>
        </section>

        <section id="why-us" className="section-shell px-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl space-y-12">
            <Reveal>
              <SectionHeading
                title={currentContent.whyUs.title}
                description={currentContent.whyUs.description}
                eyebrow={language === "fr" ? "Pourquoi Al Buraq" : "لماذا البراق"}
                testId="why-us-section"
              />
            </Reveal>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {currentContent.whyUs.items.map((item, index) => (
                <Reveal key={item.title} delay={index * 70}>
                  <article className="feature-card h-full" data-testid={`feature-card-${index + 1}`}>
                    <div className="feature-icon-wrap">
                      <i className={`${item.iconClass} text-2xl text-[#f39c12]`} aria-hidden="true" />
                    </div>
                    <h3 className={`text-2xl text-white ${titleClassName}`} data-testid={`feature-card-title-${index + 1}`}>
                      {item.title}
                    </h3>
                    <p className="text-sm leading-7 text-slate-300 sm:text-base" data-testid={`feature-card-description-${index + 1}`}>
                      {item.description}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="gallery" className="section-shell px-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl space-y-12">
            <Reveal>
              <SectionHeading
                title={currentContent.gallery.title}
                description={currentContent.gallery.description}
                eyebrow={language === "fr" ? "Ambiance visuelle" : "الأجواء البصرية"}
                testId="gallery-section"
              />
            </Reveal>

            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {(apiGallery && apiGallery.length > 0
                ? apiGallery.map(g => ({ id: `api-${g.id}`, src: g.image_path, alt: { fr: g.title_fr || 'Photo', ar: g.title_ar || 'صورة' }, title: { fr: g.title_fr || 'Photo', ar: g.title_ar || 'صورة' } }))
                : galleryItems
              ).map((item, index) => (
                <Reveal key={item.id} delay={index * 60}>
                  <button
                    type="button"
                    className="gallery-card group"
                    onClick={() => setActiveImage(item)}
                    data-testid={`gallery-item-${index + 1}`}
                  >
                    <img src={item.src} alt={item.alt[language]} className="gallery-image" loading="lazy" />
                    <div className="gallery-overlay">
                      <div>
                        <p className="text-xl font-semibold text-white" data-testid={`gallery-item-title-${index + 1}`}>
                          {item.title[language]}
                        </p>
                        <p className="text-sm text-slate-200">{currentContent.gallery.zoomHint}</p>
                      </div>
                      <span className="gallery-zoom-indicator">
                        <i className="fa-solid fa-up-right-and-down-left-from-center" aria-hidden="true" />
                      </span>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="results" className="section-shell px-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl space-y-10">
            <Reveal>
              <SectionHeading
                title={currentContent.results.title}
                description={currentContent.results.description}
                eyebrow={language === "fr" ? "Matchs" : "المباريات"}
                testId="results-section"
              />
            </Reveal>

            {/* Derniers Résultats */}
            <Reveal delay={80}>
              <div className="space-y-4">
                <h3 className={`text-xl font-semibold text-[#5dade2] ${titleClassName}`}>
                  {currentContent.results.recentTitle}
                </h3>
                <div className="space-y-3">
                  {(apiMatches && apiMatches.results && apiMatches.results.length > 0
                    ? apiMatches.results.map(m => ({ home: language === 'ar' ? m.home_team_ar : m.home_team_fr, away: language === 'ar' ? m.away_team_ar : m.away_team_fr, scoreHome: m.score_home, scoreAway: m.score_away, logoHome: m.logo_home, logoAway: m.logo_away }))
                    : currentContent.results.recentMatches
                  ).map((match, index) => (
                    <div key={index} className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-md transition-all duration-300 hover:border-white/20 hover:bg-white/8">
                      <div className="flex items-center gap-3 flex-1">
                        {match.logoHome ? (
                          <img src={match.logoHome} alt={match.home} className="h-10 w-10 rounded-full object-contain bg-white/10 p-1" />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2980b9]/20 text-sm font-bold text-[#5dade2]">{match.home.charAt(0)}</div>
                        )}
                        <span className="text-sm font-medium text-white sm:text-base">{match.home}</span>
                      </div>
                      <div className="mx-4 flex items-center gap-2 rounded-xl bg-[#0d1d31] px-4 py-2">
                        <span className="text-lg font-bold text-white">{match.scoreHome}</span>
                        <span className="text-sm text-slate-500">-</span>
                        <span className="text-lg font-bold text-white">{match.scoreAway}</span>
                      </div>
                      <div className="flex items-center gap-3 flex-1 justify-end">
                        <span className="text-sm font-medium text-white sm:text-base text-right">{match.away}</span>
                        {match.logoAway ? (
                          <img src={match.logoAway} alt={match.away} className="h-10 w-10 rounded-full object-contain bg-white/10 p-1" />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e67e22]/20 text-sm font-bold text-[#f39c12]">{match.away.charAt(0)}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Matchs à venir */}
            <Reveal delay={120}>
              <div className="space-y-4">
                <h3 className={`text-xl font-semibold text-[#f39c12] ${titleClassName}`}>
                  {currentContent.results.upcomingTitle}
                </h3>
                <div className="space-y-3">
                  {(apiMatches && apiMatches.upcoming && apiMatches.upcoming.length > 0
                    ? apiMatches.upcoming.map(m => ({ home: language === 'ar' ? m.home_team_ar : m.home_team_fr, away: language === 'ar' ? m.away_team_ar : m.away_team_fr, date: m.match_date, time: m.match_time, logoHome: m.logo_home, logoAway: m.logo_away }))
                    : currentContent.results.upcomingMatches
                  ).map((match, index) => (
                    <div key={index} className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-md transition-all duration-300 hover:border-[#f39c12]/30 hover:bg-white/8">
                      <div className="flex items-center gap-3 flex-1">
                        {match.logoHome ? (
                          <img src={match.logoHome} alt={match.home} className="h-10 w-10 rounded-full object-contain bg-white/10 p-1" />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2980b9]/20 text-sm font-bold text-[#5dade2]">{match.home.charAt(0)}</div>
                        )}
                        <span className="text-sm font-medium text-white sm:text-base">{match.home}</span>
                      </div>
                      <div className="mx-4 flex flex-col items-center rounded-xl bg-[#0d1d31] px-4 py-2">
                        <span className="text-xs text-[#f39c12] font-semibold">{match.date}</span>
                        <span className="text-sm text-slate-400">{match.time}</span>
                      </div>
                      <div className="flex items-center gap-3 flex-1 justify-end">
                        <span className="text-sm font-medium text-white sm:text-base text-right">{match.away}</span>
                        {match.logoAway ? (
                          <img src={match.logoAway} alt={match.away} className="h-10 w-10 rounded-full object-contain bg-white/10 p-1" />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e67e22]/20 text-sm font-bold text-[#f39c12]">{match.away.charAt(0)}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section id="testimonials" className="section-shell px-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl space-y-10">
            <Reveal>
              <SectionHeading
                title={currentContent.testimonials.title}
                description={currentContent.testimonials.description}
                eyebrow={language === "fr" ? "Ils nous font confiance" : "يثقون بنا"}
                testId="testimonials-section"
              />
            </Reveal>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {(apiTestimonials && apiTestimonials.length > 0
                ? apiTestimonials.map(t => ({ name: t.name, role: language === 'ar' ? t.role_ar : t.role_fr, text: language === 'ar' ? t.text_ar : t.text_fr, rating: parseInt(t.rating) }))
                : currentContent.testimonials.items
              ).map((item, index) => (
                <Reveal key={index} delay={index * 70}>
                  <div className="flex h-full flex-col rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-md transition-all duration-300 hover:border-[#f39c12]/30 hover:bg-white/8">
                    <div className="mb-4 flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className={`fa-solid fa-star text-sm ${i < item.rating ? "text-[#f39c12]" : "text-slate-600"}`} aria-hidden="true" />
                      ))}
                    </div>
                    <p className="mb-6 flex-1 text-sm leading-7 text-slate-300 italic">"{item.text}"</p>
                    <div className="flex items-center gap-3 border-t border-white/10 pt-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#2980b9] to-[#f39c12] text-sm font-bold text-white">
                        {item.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{item.name}</p>
                        <p className="text-xs text-slate-400">{item.role}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="videos" className="section-shell px-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl space-y-10">
            <Reveal>
              <SectionHeading
                title={currentContent.videos.title}
                description={currentContent.videos.description}
                eyebrow={language === "fr" ? "En action" : "في الميدان"}
                testId="videos-section"
              />
            </Reveal>

            <div className="grid gap-6 md:grid-cols-2">
              {(apiVideos && apiVideos.length > 0
                ? apiVideos.map(v => ({ title: language === 'ar' ? v.title_ar : v.title_fr, url: v.youtube_url }))
                : currentContent.videos.items
              ).map((video, index) => (
                <Reveal key={index} delay={index * 80}>
                  <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 backdrop-blur-md transition-all duration-300 hover:border-white/20">
                    <div className="aspect-video w-full">
                      <iframe
                        src={video.url}
                        title={video.title}
                        className="h-full w-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className={`text-lg font-semibold text-white ${titleClassName}`}>{video.title}</h3>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="registration" className="section-shell px-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl space-y-10">
            <Reveal>
              <SectionHeading
                title={currentContent.registration.title}
                description={currentContent.registration.description}
                eyebrow={language === "fr" ? "Rejoindre le club" : "الانضمام إلى النادي"}
                testId="registration-section"
              />
            </Reveal>

            <Reveal delay={100}>
              <RegistrationForm language={language} copy={currentContent.registration} />
            </Reveal>
          </div>
        </section>

        <section id="contact" className="section-shell px-5 pb-24 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.95fr_1.05fr]">
            <Reveal className="space-y-8 rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-md sm:p-8">
              <SectionHeading
                title={currentContent.contact.title}
                description={currentContent.contact.description}
                eyebrow={language === "fr" ? "Contact & Localisation" : "التواصل والموقع"}
                align="left"
                testId="contact-section"
              />

              <div className="grid gap-4">
                {currentContent.contact.cards.map((card, index) => (
                  <a
                    key={card.label}
                    href={card.href}
                    target={card.href.startsWith("http") ? "_blank" : undefined}
                    rel={card.href.startsWith("http") ? "noreferrer" : undefined}
                    className="contact-card"
                    data-testid={`contact-card-${index + 1}`}
                  >
                    <div className="contact-card-icon">
                      <i className={`${card.iconClass} text-lg text-[#f39c12]`} aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-400" data-testid={`contact-card-label-${index + 1}`}>
                        {card.label}
                      </p>
                      <p className="mt-1 text-base text-white sm:text-lg" data-testid={`contact-card-value-${index + 1}`}>
                        {card.value}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </Reveal>

            <Reveal delay={120} className="rounded-[2rem] border border-white/10 bg-white/5 p-3 backdrop-blur-md sm:p-4">
              <iframe
                src={mapUrl}
                title="Club location map"
                className="h-[28rem] w-full rounded-[1.5rem] border-0"
                loading="lazy"
                data-testid="location-map-iframe"
              />
            </Reveal>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 bg-[#060c15]/85 px-5 py-10 sm:px-6 lg:px-8" data-testid="site-footer">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Club Al Buraq Sportif logo" className="h-16 w-16 rounded-full object-contain ring-1 ring-white/10" loading="lazy" data-testid="footer-logo-image" />
            <div>
              <p className={`text-2xl uppercase tracking-[0.26em] text-white ${titleClassName}`} data-testid="footer-brand-title">
                {currentContent.brand}
              </p>
              <p className="text-sm text-slate-400" data-testid="footer-brand-subtitle">
                {currentContent.hero.title}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {socialLinks.map((item) => (
              <a
                key={item.id}
                href={item.href}
                target={item.href.startsWith("http") ? "_blank" : undefined}
                rel={item.href.startsWith("http") ? "noreferrer" : undefined}
                className="social-link"
                data-testid={`footer-social-link-${item.id}`}
                aria-label={item.label[language]}
              >
                <i className={item.iconClass} aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>

        <div className="mx-auto mt-6 max-w-7xl border-t border-white/10 pt-6 text-sm text-slate-400 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>{currentContent.footer.copyright}</span>
          <a href="http://7k-s.com/" target="_blank" rel="noreferrer" className="text-slate-500 transition-colors hover:text-[#5dade2]">
            Developed by <span className="font-semibold text-slate-400">7k-s.com</span>
          </a>
        </div>
      </footer>

      <a
        href="https://wa.me/212661213050"
        target="_blank"
        rel="noreferrer"
        className="floating-whatsapp"
        data-testid="floating-whatsapp-button"
        aria-label="WhatsApp"
      >
        <i className="fa-brands fa-whatsapp text-3xl" aria-hidden="true" />
      </a>

      {showScrollTop ? (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="floating-scroll-top"
          data-testid="scroll-to-top-button"
          aria-label="Scroll to top"
        >
          <i className="fa-solid fa-arrow-up" aria-hidden="true" />
        </button>
      ) : null}

      {activeImage ? (
        <div className="lightbox-backdrop" data-testid="gallery-lightbox">
          <button
            type="button"
            className="lightbox-close-button"
            onClick={() => setActiveImage(null)}
            data-testid="gallery-lightbox-close-button"
            aria-label="Close gallery preview"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
          <div className="lightbox-content">
            <img src={activeImage.src} alt={activeImage.alt[language]} className="max-h-[80vh] w-full rounded-[1.5rem] object-contain" data-testid="gallery-lightbox-image" />
            <p className="mt-4 text-center text-lg text-white" data-testid="gallery-lightbox-title">
              {activeImage.title[language]}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default App;
